import { useState } from "react";
import type { Foto } from "@farmacias/schema";
import { Field } from "./Fields";

/**
 * Upload de uma foto da landing + texto alternativo. O `alt` é editável depois
 * do envio porque o arquivo sobe antes de a pessoa descrever a imagem.
 */
export function PhotoField({
  label,
  hint,
  altHint,
  value,
  onUpload,
  onChange,
}: {
  label: string;
  hint: string;
  altHint: string;
  value: Foto | null;
  onUpload: (file: File) => Promise<void>;
  onChange: (foto: Foto | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function pick(file: File | undefined, input: HTMLInputElement) {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      await onUpload(file);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Falha no upload");
    } finally {
      setBusy(false);
      input.value = "";
    }
  }

  return (
    <div className="space-y-3">
      <Field label={label} hint={err ? undefined : hint} error={err ?? undefined}>
        <div className="flex items-start gap-4">
          {value ? (
            <img
              src={value.url}
              alt=""
              className="h-24 w-32 shrink-0 rounded-lg border border-slate-200 object-cover"
            />
          ) : (
            <div className="flex h-24 w-32 shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-400">
              sem foto
            </div>
          )}
          <div className="space-y-1">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={busy}
              className="text-sm"
              onChange={(ev) => void pick(ev.target.files?.[0], ev.target)}
            />
            {busy && <p className="text-xs text-slate-500">Enviando…</p>}
            {value && (
              <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onChange(null)}>
                Remover foto
              </button>
            )}
          </div>
        </div>
      </Field>

      {value && (
        <Field label="Texto alternativo (acessibilidade e SEO)" hint={altHint}>
          <input
            className="input"
            value={value.alt}
            onChange={(ev) => onChange({ ...value, alt: ev.target.value })}
          />
        </Field>
      )}
    </div>
  );
}
